import { useMemo, useRef, useState } from "react";
import L from "leaflet";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents
} from "react-leaflet";

const MAP_BACKGROUND_KEY = "tacops_map_backgrounds_v1";
const MAP_REAL_SETTINGS_KEY = "tacops_map_real_settings_v1";

const markerTypes = [
  "Operator",
  "Target Location",
  "Command Post",
  "Staging",
  "Perimeter",
  "Breach Point",
  "CCP / Medical",
  "EMS",
  "Overwatch",
  "Route In",
  "Route Out",
  "Containment",
  "Evidence",
  "Vehicle",
  "Hazard"
];

const tileLayers = {
  street: {
    label: "Street Map",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: "&copy; OpenStreetMap contributors"
  },
  topo: {
    label: "Topo / Terrain",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
    attribution: "Map data &copy; OpenStreetMap contributors, SRTM | Map style &copy; Esri World Topo"
  },
  satellite: {
    label: "Satellite / Aerial",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri"
  }
};

const defaultZones = [
  { id: "zone_command", type: "Command Post", label: "Command Post", x: 14, y: 72, notes: "Primary command and coordination point." },
  { id: "zone_staging", type: "Staging", label: "Primary Staging", x: 25, y: 82, notes: "Operator staging / vehicle line." },
  { id: "zone_ems", type: "CCP / Medical", label: "CCP / Medical", x: 78, y: 80, notes: "Medical staging outside immediate hazard area." },
  { id: "zone_target", type: "Target Location", label: "Target", x: 52, y: 44, notes: "Primary operation location." },
  { id: "zone_breach", type: "Breach Point", label: "Primary Breach", x: 47, y: 48, notes: "Primary entry / breach reference point." },
  { id: "zone_perimeter_a", type: "Perimeter", label: "Perimeter North", x: 50, y: 24, notes: "North containment / overwatch." },
  { id: "zone_perimeter_b", type: "Perimeter", label: "Perimeter South", x: 52, y: 64, notes: "South containment / cover unit." },
  { id: "zone_route_in", type: "Route In", label: "Route In", x: 28, y: 54, notes: "Primary approach route." },
  { id: "zone_route_out", type: "Route Out", label: "Route Out", x: 72, y: 55, notes: "Emergency egress route." }
];

function makeId(prefix = "zone") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function clampPosition(value) {
  return Math.max(3, Math.min(97, Number(value || 50)));
}

function slugType(type = "") {
  return String(type).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function loadBackgrounds() {
  try {
    return JSON.parse(localStorage.getItem(MAP_BACKGROUND_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveBackgrounds(next) {
  localStorage.setItem(MAP_BACKGROUND_KEY, JSON.stringify(next));
}

function loadRealSettings() {
  try {
    return JSON.parse(localStorage.getItem(MAP_REAL_SETTINGS_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveRealSettings(next) {
  localStorage.setItem(MAP_REAL_SETTINGS_KEY, JSON.stringify(next));
}

function cleanNumber(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function approximateLatLngFromGrid(zone, realMap) {
  const centerLat = cleanNumber(realMap.lat, 38.0861);
  const centerLon = cleanNumber(realMap.lon, -106.1417);
  const x = cleanNumber(zone.x, 50);
  const y = cleanNumber(zone.y, 50);
  const latOffset = (50 - y) * 0.00018;
  const lonOffset = (x - 50) * 0.00022;
  return [centerLat + latOffset, centerLon + lonOffset];
}

function approximateGridFromLatLng(lat, lng, realMap) {
  const centerLat = cleanNumber(realMap.lat, 38.0861);
  const centerLon = cleanNumber(realMap.lon, -106.1417);
  return {
    x: clampPosition(50 + ((cleanNumber(lng, centerLon) - centerLon) / 0.00022)),
    y: clampPosition(50 - ((cleanNumber(lat, centerLat) - centerLat) / 0.00018))
  };
}

function markerIcon(type) {
  return L.divIcon({
    className: `leaflet-tac-pin ${slugType(type)}`,
    html: `<span>${zoneLabel(type)}</span>`,
    iconSize: [46, 46],
    iconAnchor: [23, 23],
    popupAnchor: [0, -22]
  });
}

function RecenterMap({ center, zoom }) {
  const map = useMap();

  useMemo(() => {
    map.setView(center, zoom, { animate: false });
  }, [map, center[0], center[1], zoom]);

  return null;
}

function MapClickHandler({ onMapClick, onMoveEnd }) {
  const map = useMapEvents({
    click(event) {
      onMapClick(event.latlng);
    },
    moveend() {
      const center = map.getCenter();
      onMoveEnd({
        lat: Number(center.lat.toFixed(6)),
        lon: Number(center.lng.toFixed(6)),
        zoom: map.getZoom()
      });
    },
    zoomend() {
      const center = map.getCenter();
      onMoveEnd({
        lat: Number(center.lat.toFixed(6)),
        lon: Number(center.lng.toFixed(6)),
        zoom: map.getZoom()
      });
    }
  });

  return null;
}

export default function TacticalMap({ missions, warrantPlans = [], mapZones = [], setMapZones }) {
  const operations = missions || [];
  const mapRef = useRef(null);
  const dragRef = useRef(null);
  const [selectedMissionId, setSelectedMissionId] = useState(operations[0]?.id || "");
  const [selectedZoneId, setSelectedZoneId] = useState("");
  const [backgrounds, setBackgrounds] = useState(() => loadBackgrounds());
  const [realSettings, setRealSettings] = useState(() => loadRealSettings());
  const [zoneDraft, setZoneDraft] = useState({ type: "Staging", label: "", x: 50, y: 50, lat: "", lng: "", notes: "" });

  const selectedMission = useMemo(
    () => operations.find((m) => m.id === selectedMissionId) || operations[0] || null,
    [operations, selectedMissionId]
  );

  const selectedOperationId = selectedMission?.id || "default";
  const mapBackground = backgrounds[selectedOperationId] || "";
  const realMap = {
    enabled: false,
    lat: 38.0861,
    lon: -106.1417,
    zoom: 15,
    layer: "street",
    ...(realSettings[selectedOperationId] || {})
  };

  const linkedPlan = useMemo(
    () => warrantPlans.find((p) => p.operationId === selectedMission?.id || p.missionId === selectedMission?.id) || null,
    [warrantPlans, selectedMission]
  );

  const missionZones = useMemo(() => {
    const custom = mapZones.filter((z) => z.missionId === selectedMission?.id);
    if (custom.length) return custom;
    return defaultZones.map((z) => ({ ...z, id: `${selectedOperationId}_${z.id}`, missionId: selectedOperationId, isTemplate: true }));
  }, [mapZones, selectedMission, selectedOperationId]);

  const selectedZone = missionZones.find((z) => z.id === selectedZoneId);
  const activeLayer = tileLayers[realMap.layer] || tileLayers.street;
  const realMapCenter = [cleanNumber(realMap.lat, 38.0861), cleanNumber(realMap.lon, -106.1417)];
  const realMapZoom = cleanNumber(realMap.zoom, 15);

  function commitBackground(value) {
    const next = { ...backgrounds, [selectedOperationId]: value };
    setBackgrounds(next);
    saveBackgrounds(next);
  }

  function updateRealMap(patch) {
    const next = {
      ...realSettings,
      [selectedOperationId]: { ...realMap, ...patch }
    };
    setRealSettings(next);
    saveRealSettings(next);
  }

  function materializeTemplateOverlay(preferredZoneId = "") {
    const existingCustom = mapZones.filter((z) => z.missionId === selectedOperationId);

    // If custom markers already exist, do not overwrite them.
    if (existingCustom.length) {
      return existingCustom.find((z) => z.id === preferredZoneId) || null;
    }

    const seeded = defaultZones.map((z) => ({
      ...z,
      id: makeId("zone"),
      missionId: selectedOperationId,
      isTemplate: false
    }));

    setMapZones([...seeded, ...mapZones.filter((z) => z.missionId !== selectedOperationId)]);

    const preferredTemplate = missionZones.find((z) => z.id === preferredZoneId);
    const matched =
      seeded.find((z) => z.type === preferredTemplate?.type && z.label === preferredTemplate?.label) ||
      seeded[0] ||
      null;

    if (matched) setSelectedZoneId(matched.id);
    return matched;
  }

  function ensureEditableZone(zone) {
    if (!zone) return null;
    if (!zone.isTemplate) return zone;
    return materializeTemplateOverlay(zone.id);
  }

  function addZone(e) {
    e.preventDefault();
    if (!zoneDraft.label.trim()) return alert("Marker label is required.");

    const lat = zoneDraft.lat !== "" ? cleanNumber(zoneDraft.lat, realMapCenter[0]) : realMapCenter[0];
    const lng = zoneDraft.lng !== "" ? cleanNumber(zoneDraft.lng, realMapCenter[1]) : realMapCenter[1];
    const gridFromGeo = approximateGridFromLatLng(lat, lng, realMap);

    const zone = {
      ...zoneDraft,
      id: makeId("zone"),
      missionId: selectedOperationId,
      x: realMap.enabled ? gridFromGeo.x : clampPosition(zoneDraft.x),
      y: realMap.enabled ? gridFromGeo.y : clampPosition(zoneDraft.y),
      lat,
      lng,
      notes: zoneDraft.notes.trim()
    };

    setMapZones([zone, ...mapZones]);
    setSelectedZoneId(zone.id);
    setZoneDraft({ type: "Staging", label: "", x: 50, y: 50, lat: "", lng: "", notes: "" });
  }

  function updateZone(id, patch) {
    const current = missionZones.find((z) => z.id === id);
    const editable = ensureEditableZone(current);
    if (!editable) return;

    const source = mapZones.some((z) => z.id === editable.id) ? mapZones : [editable, ...mapZones];

    setMapZones(source.map((z) => {
      if (z.id !== editable.id) return z;
      return {
        ...z,
        ...patch,
        x: patch.x !== undefined ? clampPosition(patch.x) : z.x,
        y: patch.y !== undefined ? clampPosition(patch.y) : z.y
      };
    }));
  }

  function deleteZone(id) {
    const current = missionZones.find((z) => z.id === id);
    if (!current) return;
    if (current.isTemplate) {
      alert("This is a starter template marker. Move or edit it first, or add your own operation marker.");
      return;
    }
    if (!confirm("Delete this map marker?")) return;
    setMapZones(mapZones.filter((z) => z.id !== id));
    if (selectedZoneId === id) setSelectedZoneId("");
  }

  function clearOperationMap() {
    if (!selectedMission) return;
    if (!confirm("Clear all custom map markers for this operation and restore the starter overlay?")) return;
    setMapZones(mapZones.filter((z) => z.missionId !== selectedOperationId));
    setSelectedZoneId("");
  }

  function seedDefaultOverlay() {
    const seeded = defaultZones.map((z) => {
      const [lat, lng] = approximateLatLngFromGrid(z, realMap);
      return { ...z, id: makeId("zone"), missionId: selectedOperationId, isTemplate: false, lat, lng };
    });
    setMapZones([...seeded, ...mapZones.filter((z) => z.missionId !== selectedOperationId)]);
    setSelectedZoneId(seeded[0]?.id || "");
  }

  function mapPointFromEvent(event) {
    const rect = mapRef.current?.getBoundingClientRect();
    if (!rect) return { x: 50, y: 50 };
    const clientX = event.clientX ?? event.touches?.[0]?.clientX;
    const clientY = event.clientY ?? event.touches?.[0]?.clientY;
    return {
      x: clampPosition(((clientX - rect.left) / rect.width) * 100),
      y: clampPosition(((clientY - rect.top) / rect.height) * 100)
    };
  }

  function startDrag(event, zone) {
    event.preventDefault();
    event.stopPropagation();
    const editable = ensureEditableZone(zone);
    dragRef.current = editable?.id || zone.id;
    setSelectedZoneId(editable?.id || zone.id);
    window.addEventListener("pointermove", handleDragMove);
    window.addEventListener("pointerup", stopDrag);
  }

  function handleDragMove(event) {
    const id = dragRef.current;
    if (!id) return;
    const point = mapPointFromEvent(event);
    updateZone(id, point);
  }

  function stopDrag() {
    dragRef.current = null;
    window.removeEventListener("pointermove", handleDragMove);
    window.removeEventListener("pointerup", stopDrag);
  }

  function addMarkerAtMapClick(event) {
    if (event.target.closest(".map-pin")) return;
    const point = mapPointFromEvent(event);
    setZoneDraft((prev) => ({ ...prev, x: Math.round(point.x), y: Math.round(point.y) }));
  }

  function handleRealMapClick(latlng) {
    setZoneDraft((prev) => ({
      ...prev,
      lat: Number(latlng.lat.toFixed(6)),
      lng: Number(latlng.lng.toFixed(6))
    }));
  }

  function handleRealMarkerDrag(zone, event) {
    const editable = ensureEditableZone(zone);
    if (!editable) return;

    const marker = event.target;
    const latlng = marker.getLatLng();
    const grid = approximateGridFromLatLng(latlng.lat, latlng.lng, realMap);

    updateZone(editable.id, {
      lat: Number(latlng.lat.toFixed(6)),
      lng: Number(latlng.lng.toFixed(6)),
      x: grid.x,
      y: grid.y
    });
  }

  function zoneLatLng(zone) {
    if (zone.lat !== undefined && zone.lng !== undefined && zone.lat !== "" && zone.lng !== "") {
      return [cleanNumber(zone.lat, realMapCenter[0]), cleanNumber(zone.lng, realMapCenter[1])];
    }
    return approximateLatLngFromGrid(zone, realMap);
  }

  const rosterAssignments = useMemo(() => {
    try {
      const raw = localStorage.getItem("tacops_assignments_v1");
      const parsed = raw ? JSON.parse(raw) : {};
      const current = parsed[selectedOperationId] || parsed.default || [];
      return Array.isArray(current) ? current : [];
    } catch {
      return [];
    }
  }, [selectedOperationId]);

  const assignedOperators = useMemo(() => {
    const names = new Set();

    rosterAssignments.forEach((assignment) => {
      const possible =
        assignment.operator ||
        assignment.operatorName ||
        assignment.name ||
        assignment.assignedTo ||
        assignment.member ||
        assignment.personnel;

      if (possible && String(possible).trim()) {
        names.add(String(possible).trim());
      }
    });

    mapZones
      .filter((z) => z.missionId === selectedOperationId && z.type === "Operator")
      .forEach((z) => {
        if (z.label) names.add(z.label);
      });

    return Array.from(names).sort();
  }, [rosterAssignments, mapZones, selectedOperationId]);

  function stageOperator(operatorName) {
    if (!operatorName) return;

    const already = mapZones.find(
      (z) =>
        z.missionId === selectedOperationId &&
        z.type === "Operator" &&
        z.label === operatorName
    );

    if (already) {
      setSelectedZoneId(already.id);
      return;
    }

    const offset = mapZones.filter(
      (z) => z.missionId === selectedOperationId && z.type === "Operator"
    ).length;

    const zone = {
      id: makeId("operator"),
      missionId: selectedOperationId,
      type: "Operator",
      label: operatorName,
      x: clampPosition(42 + offset * 3),
      y: clampPosition(52 + offset * 3),
      notes: "Personnel marker staged from Assignment Board."
    };

    setMapZones([zone, ...mapZones]);
    setSelectedZoneId(zone.id);
  }

  function stageAllAssignedOperators() {
    if (!assignedOperators.length) {
      alert("No assigned personnel were found yet. Add assignments first, then return to the map.");
      return;
    }

    const existingNames = new Set(
      mapZones
        .filter((z) => z.missionId === selectedOperationId && z.type === "Operator")
        .map((z) => z.label)
    );

    const additions = assignedOperators
      .filter((name) => !existingNames.has(name))
      .map((name, index) => ({
        id: makeId("operator"),
        missionId: selectedOperationId,
        type: "Operator",
        label: name,
        x: clampPosition(38 + index * 4),
        y: clampPosition(54 + index * 4),
        notes: "Personnel marker staged from Assignment Board."
      }));

    if (!additions.length) {
      alert("All assigned operators are already staged on the map.");
      return;
    }

    setMapZones([...additions, ...mapZones]);
    setSelectedZoneId(additions[0]?.id || "");
  }

  function exportMapBrief() {
    const esc = (value) => String(value || "").replace(/[&<>"']/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" }[m]));
    const win = window.open("", "_blank");
    if (!win) return alert("Popup blocked. Allow popups and try again.");

    win.document.write(`
      <html><head><title>TacOps Tactical Map - ${esc(selectedMission?.title)}</title>
      <style>
        body{font-family:Arial,sans-serif;margin:34px;color:#111;background:#fff}button{padding:8px 12px;margin-bottom:14px}h1{margin:0 0 4px;letter-spacing:.02em}.sub{color:#555;margin-bottom:20px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}.box{border:1px solid #bbb;padding:10px;margin:8px 0;page-break-inside:avoid}.label{font-size:11px;color:#555;text-transform:uppercase;font-weight:bold}.value{margin-top:4px;white-space:pre-wrap;line-height:1.35}.map{position:relative;height:520px;border:2px solid #111;margin:16px 0;background:${mapBackground ? `url('${esc(mapBackground)}') center/cover no-repeat` : "linear-gradient(90deg,#eee 1px,transparent 1px),linear-gradient(#eee 1px,transparent 1px),#f8f8f8"};background-size:${mapBackground ? "cover" : "40px 40px,40px 40px,auto"};overflow:hidden}.pin{position:absolute;transform:translate(-50%,-50%);width:34px;height:34px;border-radius:50%;display:grid;place-items:center;background:#111;color:#fff;font-weight:bold;font-size:10px;border:2px solid #fff;box-shadow:0 0 0 1px #111}.legend{display:flex;flex-wrap:wrap;gap:10px;margin:8px 0 18px}.legend span{border:1px solid #ccc;padding:5px 8px;border-radius:999px;font-size:12px}.print-compass{position:absolute;right:12px;top:12px;width:70px;height:70px;border:1px solid #111;background:rgba(255,255,255,.85);display:grid;place-items:center;font-weight:bold}.row{display:flex;justify-content:space-between;gap:12px}@media print{button{display:none}body{margin:22px}.box{break-inside:avoid}}
      </style></head><body>
      <button onclick="window.print()">Print / Save as PDF</button>
      <h1>FAST Tactical Map / Overlay Brief</h1><div class="sub">TacOps by Aegis Group • Generated ${esc(new Date().toLocaleString())}</div>
      <div class="grid">
        <div class="box"><div class="label">Operation</div><div class="value">${esc(selectedMission?.title)}</div></div>
        <div class="box"><div class="label">Type</div><div class="value">${esc(selectedMission?.operationType || selectedMission?.missionType)}</div></div>
        <div class="box"><div class="label">Location</div><div class="value">${esc(selectedMission?.location)}</div></div>
        <div class="box"><div class="label">Commander</div><div class="value">${esc(selectedMission?.commander)}</div></div>
      </div>
      <div class="map">
        <div class="print-compass"><div><div style="text-align:center">N</div><div style="text-align:center">↑</div><div class="row"><span>W</span><span>✚</span><span>E</span></div><div style="text-align:center">S</div></div></div>
        ${missionZones.map((z) => `<div class="pin" style="left:${esc(z.x)}%;top:${esc(z.y)}%">${esc(zoneLabel(z.type))}</div>`).join("")}
      </div>
      <div class="legend">${markerTypes.map((t) => `<span><b>${esc(zoneLabel(t))}</b> ${esc(t)}</span>`).join("")}</div>
      ${realMap.enabled ? `<h2>Map Coordinates</h2><div class="box"><div class="label">Map Center / Zoom / Layer</div><div class="value">Lat: ${esc(realMap.lat)}\nLon: ${esc(realMap.lon)}\nZoom: ${esc(realMap.zoom)}\nLayer: ${esc(tileLayers[realMap.layer]?.label || "Street Map")}</div></div>` : ""}
      ${linkedPlan ? `<h2>Linked Plan Notes</h2><div class="box"><div class="label">Threat / Staging / Routes</div><div class="value">${esc(linkedPlan.threatAssessment)}\n\nStaging: ${esc(linkedPlan.staging)}\nRoute In: ${esc(linkedPlan.routeIn)}\nRoute Out: ${esc(linkedPlan.routeOut)}</div></div>` : ""}
      <h2>Markers</h2>
      ${missionZones.map((z) => `<div class="box"><div class="label">${esc(zoneLabel(z.type))} • ${esc(z.type)} • ${esc(z.label)}</div><div class="value">Grid: X ${esc(Math.round(z.x))} / Y ${esc(Math.round(z.y))}${z.lat && z.lng ? `\nLat/Lon: ${esc(z.lat)}, ${esc(z.lng)}` : ""}\n${esc(z.notes)}</div></div>`).join("")}
      </body></html>
    `);
    win.document.close();
  }

  return (
    <section>
      <div className="section-head">
        <div>
          <h2>Tactical Map / Overlay Board</h2>
          <p className="muted">Drag markers, build operation-specific overlays, add tactical notes, and export a printable map brief.</p>
        </div>
        <div className="map-toolbar">
          <button onClick={seedDefaultOverlay}>Load FAST Template</button>
          <button onClick={clearOperationMap} className="danger-text">Clear Operation Map</button>
          <button onClick={stageAllAssignedOperators}>Stage Assigned Operators</button>
          <button className="primary" onClick={exportMapBrief}>Print / Save PDF</button>
        </div>
      </div>

      <div className="map-layout upgraded-map-layout">
        <div className="panel map-workspace-panel">
          <div className="map-control-strip">
            <label>
              <span>Operation</span>
              <select value={selectedMissionId} onChange={(e) => { setSelectedMissionId(e.target.value); setSelectedZoneId(""); }}>
                {operations.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
              </select>
            </label>
            <label>
              <span>Map Mode</span>
              <select value={realMap.enabled ? "real" : "grid"} onChange={(e) => updateRealMap({ enabled: e.target.value === "real" })}>
                <option value="grid">Tactical Grid / Image Overlay</option>
                <option value="real">Interactive Street / Satellite Map</option>
              </select>
            </label>
            <label>
              <span>Optional Background / Satellite Image URL</span>
              <input value={mapBackground} onChange={(e) => commitBackground(e.target.value)} placeholder="Paste image URL or leave blank for tactical grid" disabled={realMap.enabled} />
            </label>
          </div>

          {realMap.enabled && (
            <div className="real-map-controls">
              <label>
                <span>Latitude</span>
                <input type="number" step="0.000001" value={realMap.lat} onChange={(e) => updateRealMap({ lat: e.target.value })} />
              </label>
              <label>
                <span>Longitude</span>
                <input type="number" step="0.000001" value={realMap.lon} onChange={(e) => updateRealMap({ lon: e.target.value })} />
              </label>
              <label>
                <span>Zoom</span>
                <select value={realMap.zoom} onChange={(e) => updateRealMap({ zoom: Number(e.target.value) })}>
                  <option value="12">12 - Area</option>
                  <option value="13">13 - Area Close</option>
                  <option value="14">14 - Neighborhood</option>
                  <option value="15">15 - Street</option>
                  <option value="16">16 - Building</option>
                  <option value="17">17 - Close</option>
                  <option value="18">18 - Detail</option>
                </select>
              </label>
              <label>
                <span>Base Layer</span>
                <select value={realMap.layer} onChange={(e) => updateRealMap({ layer: e.target.value })}>
                  {Object.entries(tileLayers).map(([key, layer]) => <option key={key} value={key}>{layer.label}</option>)}
                </select>
              </label>
            </div>
          )}

          {realMap.enabled ? (
            <div className="leaflet-map-shell">
              <MapContainer
                center={realMapCenter}
                zoom={realMapZoom}
                scrollWheelZoom={true}
                className="leaflet-real-map"
              >
                <RecenterMap center={realMapCenter} zoom={realMapZoom} />
                <MapClickHandler onMapClick={handleRealMapClick} onMoveEnd={updateRealMap} />
                <TileLayer attribution={activeLayer.attribution} url={activeLayer.url} />
                {missionZones.map((zone) => (
                  <Marker
                    key={zone.id}
                    position={zoneLatLng(zone)}
                    icon={markerIcon(zone.type)}
                    draggable={true}
                    eventHandlers={{
                      click: () => setSelectedZoneId(zone.id),
                      dragend: (event) => handleRealMarkerDrag(zone, event)
                    }}
                  >
                    <Popup>
                      <strong>{zone.label || zone.type}</strong>
                      <br />
                      {zone.type}
                      <br />
                      <small>{zone.notes}</small>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>

              <div className="map-direction-legend leaflet-compass" aria-label="Map direction legend">
                <div className="compass-n">N</div>
                <div className="compass-row">
                  <span>W</span>
                  <span className="compass-core">✚</span>
                  <span>E</span>
                </div>
                <div className="compass-arrow">↑</div>
                <div className="compass-s">S</div>
              </div>
            </div>
          ) : (
            <div
              className="tactical-map upgraded-tactical-map"
              ref={mapRef}
              onPointerDown={addMarkerAtMapClick}
              style={mapBackground ? { backgroundImage: `linear-gradient(rgba(5,10,15,.22), rgba(5,10,15,.22)), url(${mapBackground})` } : undefined}
            >
              <div className="map-grid-lines" />
              <div className="map-crosshair horizontal" />
              <div className="map-crosshair vertical" />
              <div className="map-direction-legend" aria-label="Map direction legend">
                <div className="compass-n">N</div>
                <div className="compass-row">
                  <span>W</span>
                  <span className="compass-core">✚</span>
                  <span>E</span>
                </div>
                <div className="compass-arrow">↑</div>
                <div className="compass-s">S</div>
              </div>

              {missionZones.map((zone) => (
                <button
                  key={zone.id}
                  className={`map-pin ${slugType(zone.type)} ${selectedZoneId === zone.id ? "selected" : ""}`}
                  style={{ left: `${zone.x}%`, top: `${zone.y}%` }}
                  title={`${zone.type}: ${zone.label}`}
                  onPointerDown={(e) => startDrag(e, zone)}
                  onClick={() => setSelectedZoneId(zone.id)}
                >
                  <span>{zoneLabel(zone.type)}</span>
                </button>
              ))}
            </div>
          )}

          <div className="map-legend upgraded-map-legend">
            {markerTypes.map((type) => <span key={type}><b className={`legend-dot ${slugType(type)}`} /> {type}</span>)}
          </div>
        </div>

        <div className="panel map-side-panel">
          <h2>Marker Details</h2>
          {selectedZone ? (
            <div className="selected-zone marker-editor">
              <div className="operation-title">{selectedZone.label}</div>
              <div className="operation-meta">
                {selectedZone.type} • X {Math.round(selectedZone.x)} / Y {Math.round(selectedZone.y)}
                {selectedZone.lat && selectedZone.lng ? ` • ${Number(selectedZone.lat).toFixed(5)}, ${Number(selectedZone.lng).toFixed(5)}` : ""}
              </div>

              <div className="form-grid">
                <label>
                  <span>Type</span>
                  <select value={selectedZone.type} onChange={(e) => updateZone(selectedZone.id, { type: e.target.value })}>
                    {markerTypes.map((type) => <option key={type}>{type}</option>)}
                  </select>
                </label>
                <label>
                  <span>Label</span>
                  <input value={selectedZone.label} onChange={(e) => updateZone(selectedZone.id, { label: e.target.value })} />
                </label>
                <label>
                  <span>X Position</span>
                  <input type="number" value={Math.round(selectedZone.x)} onChange={(e) => updateZone(selectedZone.id, { x: e.target.value })} />
                </label>
                <label>
                  <span>Y Position</span>
                  <input type="number" value={Math.round(selectedZone.y)} onChange={(e) => updateZone(selectedZone.id, { y: e.target.value })} />
                </label>
                <label>
                  <span>Latitude</span>
                  <input type="number" step="0.000001" value={selectedZone.lat || ""} onChange={(e) => updateZone(selectedZone.id, { lat: e.target.value })} />
                </label>
                <label>
                  <span>Longitude</span>
                  <input type="number" step="0.000001" value={selectedZone.lng || ""} onChange={(e) => updateZone(selectedZone.id, { lng: e.target.value })} />
                </label>
              </div>

              <label className="full">
                <span>Marker Notes</span>
                <textarea rows="4" value={selectedZone.notes || ""} onChange={(e) => updateZone(selectedZone.id, { notes: e.target.value })} />
              </label>

              <button className="danger-text" onClick={() => deleteZone(selectedZone.id)}>Delete Marker</button>
            </div>
          ) : (
            <div className="empty">Select a marker, drag a marker, or click the map to stage a new marker position.</div>
          )}

          <hr className="soft-rule" />
          <h2>Assigned Personnel</h2>
          <p className="muted">Stage assigned operators from the Assignment Board onto this map, then drag them into position.</p>
          {assignedOperators.length ? (
            <div className="operator-stage-list">
              {assignedOperators.map((name) => (
                <button key={name} onClick={() => stageOperator(name)}>
                  Stage {name}
                </button>
              ))}
            </div>
          ) : (
            <div className="empty">No assignment-board personnel found for this operation yet.</div>
          )}

          <hr className="soft-rule" />
          <h2>Add Marker</h2>
          <form onSubmit={addZone} className="map-add-form">
            <div className="form-grid">
              <label>
                <span>Type</span>
                <select value={zoneDraft.type} onChange={(e) => setZoneDraft({ ...zoneDraft, type: e.target.value })}>
                  {markerTypes.map((type) => <option key={type}>{type}</option>)}
                </select>
              </label>
              <label>
                <span>Label</span>
                <input value={zoneDraft.label} onChange={(e) => setZoneDraft({ ...zoneDraft, label: e.target.value })} placeholder="Primary staging" />
              </label>
              <label>
                <span>X</span>
                <input type="number" value={zoneDraft.x} onChange={(e) => setZoneDraft({ ...zoneDraft, x: e.target.value })} />
              </label>
              <label>
                <span>Y</span>
                <input type="number" value={zoneDraft.y} onChange={(e) => setZoneDraft({ ...zoneDraft, y: e.target.value })} />
              </label>
              <label>
                <span>Latitude</span>
                <input type="number" step="0.000001" value={zoneDraft.lat} onChange={(e) => setZoneDraft({ ...zoneDraft, lat: e.target.value })} placeholder={String(realMapCenter[0])} />
              </label>
              <label>
                <span>Longitude</span>
                <input type="number" step="0.000001" value={zoneDraft.lng} onChange={(e) => setZoneDraft({ ...zoneDraft, lng: e.target.value })} placeholder={String(realMapCenter[1])} />
              </label>
            </div>
            <label className="full">
              <span>Notes</span>
              <textarea rows="3" value={zoneDraft.notes} onChange={(e) => setZoneDraft({ ...zoneDraft, notes: e.target.value })} placeholder="Assignment, route, cover position, tactical note..." />
            </label>
            <button className="primary" type="submit">Add Marker</button>
          </form>
        </div>
      </div>
    </section>
  );
}

function zoneLabel(type) {
  const map = {
    "Operator": "OP",
    "Command Post": "CP",
    "Staging": "S",
    "EMS": "EMS",
    "CCP / Medical": "CCP",
    "Target Location": "T",
    "Perimeter": "P",
    "Breach Point": "BP",
    "Route In": "IN",
    "Route Out": "OUT",
    "Overwatch": "OW",
    "Containment": "C",
    "Evidence": "E",
    "Vehicle": "V",
    "Hazard": "HZ"
  };
  return map[type] || "•";
}
